import { TinyColor } from '@ctrl/tinycolor'
import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function ChatboxLayout({
  children,
  clipScene,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp, sl } = useOrientation(orientation)

  return (
    <AbsoluteFill
      className={cn('justify-between', s && 'p-[50px]', lp && 'p-[60px]')}
    >
      <div
        className={cn(
          'flex items-center',
          s && 'space-x-[34px]',
          lp && 'space-x-[50px]',
        )}
      >
        <FallbackImg
          src={clipScene.imageUrl}
          className={cn(
            'aspect-square object-cover',
            s && 'h-[136px]',
            lp && 'h-[165px]',
          )}
        />

        <div
          className={cn(
            'leading-[1.52]',
            p && 'text-[36px]',
            s && 'text-[34px]',
            l && 'text-[38px]',
          )}
        >
          <p
            style={{ ...style.title }}
            className={cn(
              'text-left font-[600]',
              p && 'line-clamp-2',
              sl && 'line-clamp-1',
            )}
          >
            {clipScene.title}
          </p>

          <p
            style={{ ...style.subtitle }}
            className="line-clamp-1 text-left font-[500]"
          >
            {clipScene.subtitle}
          </p>
        </div>
      </div>

      <div
        className={cn(
          'relative flex h-full flex-col justify-between border',
          p && 'mt-[73px] mb-[150px]',
          s && 'mt-[48px] mb-[110px] p-[60px]',
          l && 'mt-[46px] mb-[80px]',
          lp && 'p-[80px]',
        )}
        style={{
          borderColor: new TinyColor(style.border.color)
            .setAlpha(0.5)
            .toRgbString(),
        }}
      >
        <div>{children}</div>

        <BarsVisualization
          className="z-20"
          color={style.waveform.color}
          gapSize={orientation === 'landscape' ? 7 : 4}
          height={
            orientation === 'portrait'
              ? 320
              : orientation === 'landscape'
                ? 200
                : 240
          }
          lineThickness={orientation === 'landscape' ? 8 : 3}
          mediaUrl={sceneAudioSrc(clipScene)}
          waveformUrl={clipScene.waveformUrl}
          mediaStart={clipScene.mediaStart}
          placement="under"
        />

        <div
          style={{ borderLeftColor: style.border.color }}
          className={cn(
            'absolute left-[100px] border-y-transparent opacity-50',
            p && '-bottom-[100px] border-b-[100px] border-l-[100px]',
            sl && '-bottom-[70px] border-b-[70px] border-l-[70px]',
          )}
        />

        <div
          style={{ borderLeftColor: style.background.color }}
          className={cn(
            'absolute left-[102px] border-y-transparent',
            p && '-bottom-[96px] border-b-[100px] border-l-[101px]',
            sl && '-bottom-[66px] border-b-[70px] border-l-[71px]',
          )}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-[37px]">
          <FallbackImg
            className="aspect-square h-[70px] rounded-full"
            src={speaker?.avatarUrl}
          />

          <p
            style={{ ...style.speakerUsername }}
            className={cn(
              'leading-[1.3]',
              s && 'text-[34px]',
              lp && 'text-[42px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        {clipScene.showZealousLogo && (
          <ZealousSvg
            color={style.logo.color}
            className="h-[49px] opacity-50"
          />
        )}
      </div>
    </AbsoluteFill>
  )
}

export const chatboxLayoutInfo: LayoutInfo = {
  name: 'chatbox',
  Component: ChatboxLayout,
  defaultScene: {
    ...defaultClipScene,
    layout: 'chatbox',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'secondary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
      scrollType: 'page-by-page',
      fontFamily: 'primary',
      highlightType: 'last-word',
      fontSize: 50,
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.6,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 54,
      lineHeight: 1.52,
      linesPerPage: 3,
    },
    portrait: {
      fontSize: 64,
      lineHeight: 1.75,
      linesPerPage: 7,
    },
  },
}
